/**
 * Emoji Font Detector
 * Detects device type and applies appropriate emoji fonts
 */

export interface DeviceInfo {
  isMobile: boolean;
  isAndroid: boolean;
  isIOS: boolean;
  isDesktop: boolean;
  browser: string;
  platform: string;
}

export class EmojiDetector {
  private deviceInfo: DeviceInfo;

  constructor() {
    this.deviceInfo = this.detectDevice();
    this.applyEmojiFont();
  }

  /**
   * Detect device and browser information
   */
  private detectDevice(): DeviceInfo {
    // Return default values for SSR
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return {
        isMobile: false,
        isAndroid: false,
        isIOS: false,
        isDesktop: true,
        browser: 'unknown',
        platform: 'server'
      };
    }

    const userAgent = navigator.userAgent.toLowerCase();
    const platform = navigator.platform.toLowerCase();

    const isAndroid = /android/.test(userAgent);
    const isIOS = /iphone|ipad|ipod/.test(userAgent) || (platform === 'macintel' && navigator.maxTouchPoints > 1);
    const isMobile = isAndroid || isIOS || /mobile|tablet/.test(userAgent);
    const isDesktop = !isMobile;

    // Detect browser
    let browser = 'unknown';
    if (/chrome/.test(userAgent) && !/edg/.test(userAgent)) {
      browser = 'chrome';
    } else if (/firefox/.test(userAgent)) {
      browser = 'firefox';
    } else if (/safari/.test(userAgent) && !/chrome/.test(userAgent)) {
      browser = 'safari';
    } else if (/edg/.test(userAgent)) {
      browser = 'edge';
    }

    return {
      isMobile,
      isAndroid,
      isIOS,
      isDesktop,
      browser,
      platform
    };
  }

  /**
   * Apply appropriate emoji font based on device
   */
  private applyEmojiFont(): void {
    // Skip for SSR
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    
    // Remove any existing emoji classes
    document.body.classList.remove('emoji-mobile', 'emoji-desktop', 'emoji-android', 'emoji-ios');

    if (this.deviceInfo.isAndroid) {
      // Android: Use system emojis
      root.style.setProperty('--emoji-font-active', 'system-emoji, emoji, "Roboto", sans-serif');
      root.style.setProperty('--emoji-monospace-active', 'system-emoji, emoji, "Roboto", monospace');
      document.body.classList.add('emoji-mobile', 'emoji-android');
      
    } else if (this.deviceInfo.isIOS) {
      // iOS: Use Apple Color Emoji
      root.style.setProperty('--emoji-font-active', '"Apple Color Emoji", -apple-system-emoji, system-emoji, emoji, sans-serif');
      root.style.setProperty('--emoji-monospace-active', '"Apple Color Emoji", -apple-system-emoji, system-emoji, emoji, monospace');
      document.body.classList.add('emoji-mobile', 'emoji-ios');
      
    } else if (this.deviceInfo.isDesktop) {
      // Desktop: Use Noto Color Emoji
      root.style.setProperty('--emoji-font-active', '"Noto Color Emoji", "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Emoji", sans-serif');
      root.style.setProperty('--emoji-monospace-active', '"Noto Color Emoji", "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Emoji", monospace');
      document.body.classList.add('emoji-desktop');
      
      // Ensure Noto fonts are loaded
      this.preloadNotoFonts();
    }

    // Set data attributes for CSS targeting
    document.body.setAttribute('data-device-type', this.deviceInfo.isMobile ? 'mobile' : 'desktop');
    document.body.setAttribute('data-platform', this.deviceInfo.isAndroid ? 'android' : this.deviceInfo.isIOS ? 'ios' : 'desktop');

    console.log('Emoji detector applied:', this.deviceInfo);
  }

  /**
   * Preload Noto Color Emoji fonts for desktop
   */
  private preloadNotoFonts(): void {
    // Skip for SSR
    if (typeof document === 'undefined') return;

    if (this.deviceInfo.isDesktop) {
      const link1 = document.createElement('link');
      link1.rel = 'preload';
      link1.href = 'https://fonts.googleapis.com/css2?family=Noto+Color+Emoji&display=swap';
      link1.as = 'style';
      link1.onload = () => {
        link1.rel = 'stylesheet';
      };
      document.head.appendChild(link1);

      const link2 = document.createElement('link');
      link2.rel = 'preload';
      link2.href = 'https://fonts.googleapis.com/css2?family=Noto+Emoji:wght@300;400;500;600;700&display=swap';
      link2.as = 'style';
      link2.onload = () => {
        link2.rel = 'stylesheet';
      };
      document.head.appendChild(link2);
    }
  }

  /**
   * Get device information
   */
  public getDeviceInfo(): DeviceInfo {
    return this.deviceInfo;
  }

  /**
   * Force refresh emoji detection (useful after dynamic content changes)
   */
  public refresh(): void {
    this.applyEmojiFont();
  }

  /**
   * Test emoji rendering with a sample
   */
  public testEmojiRendering(): string {
    const testEmojis = '🌱♻️🌍🗑️📱💡🥤📄🥫🍾';
    return `Device: ${this.deviceInfo.isAndroid ? 'Android' : this.deviceInfo.isIOS ? 'iOS' : 'Desktop'} | Test: ${testEmojis}`;
  }
}

// Auto-initialize when imported (only in browser)
let emojiDetector: EmojiDetector;

export function initializeEmojiDetector(): EmojiDetector {
  if (typeof window === 'undefined') {
    // Return a dummy instance for SSR
    return new EmojiDetector();
  }
  if (!emojiDetector) {
    emojiDetector = new EmojiDetector();
  }
  return emojiDetector;
}

export function getEmojiDetector(): EmojiDetector | null {
  return emojiDetector || null;
}
